// src/admin/hooks/useLoans.ts
// Lists loans for the admin queue, joined to their borrower profile so we can
// show the name without a second fetch. Default order is newest applied first.
// Cap the result set at 200 rows — the screen paginates client-side by filter,
// and anything larger than that implies a filter should have been applied.
//
// Note: the generated types declare empty Relationships arrays (gen-types.ts
// does not parse FK metadata), so supabase-js can't strongly-type the joined
// shape. We describe the row shape explicitly via LoanRow + cast on return.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import type { Database } from '@/lib/database.types';

export type LoanRow = Database['public']['Tables']['loans']['Row'] & {
  profiles: { display_name: string | null; full_name: string | null; phone: string | null } | null;
};

export interface LoanFilter {
  status?: Database['public']['Enums']['loan_status'] | 'all';
  search?: string;
}

export function useLoans(filter: LoanFilter) {
  return useQuery({
    queryKey: ['loans', filter],
    queryFn: async (): Promise<LoanRow[]> => {
      let q = supabase
        .from('loans')
        .select('*, profiles(display_name, full_name, phone)')
        .order('applied_at', { ascending: false })
        .limit(200);
      if (filter.status && filter.status !== 'all') {
        q = q.eq('status', filter.status);
      }
      if (filter.search && filter.search.length > 0) {
        // loan_number is always indexable; match on display_name / full_name
        // uses postgrest's "referenced-table.column" syntax for joined filters.
        const s = filter.search.replace(/[%_]/g, '\\$&');
        q = q.or(
          `loan_number.ilike.%${s}%,profiles.display_name.ilike.%${s}%,profiles.full_name.ilike.%${s}%`,
        );
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as LoanRow[];
    },
  });
}
